import { Link } from "react-router-dom";
import { Container, FadeIn, SectionDivider } from "@/components/Section";
import SEOHead from "@/components/SEOHead";
import StatusBadge from "@/components/StatusBadge";
import StayConnected from "@/components/StayConnected";
import { groupedProjects } from "@/lib/projects";
import { ArrowRight, ExternalLink } from "lucide-react";
import { TOK_ASSETS } from "@/lib/data";

/**
 * /projects — the Temple of Kulture ecosystem canvas.
 *
 * Four columns (Current · In Dev · Planned · Future Vision) showing
 * every immersive show, programme idea and capital project that
 * makes up the TOK vision. Single source of truth lives in
 * /app/frontend/src/lib/projects.js.
 */
export default function Projects() {
  const groups = groupedProjects();

  return (
    <div data-testid="projects-page" className="bg-black">
      <SEOHead
        title="Current Projects, In Development & Future Vision — Temple of Kulture"
        description="The Temple of Kulture project ecosystem — Windrush, Buro Buro Mermaid, Yekri, Dogma, Waterline and more. Explore live works, what's coming, and how to partner."
        image={TOK_ASSETS.windrushBanner}
      />

      {/* HERO */}
      <section
        data-testid="projects-hero"
        className="relative pt-36 pb-24 sm:pt-44 sm:pb-32 overflow-hidden"
      >
        <div
          className="absolute inset-0 bg-cover"
          style={{ backgroundImage: `url(${TOK_ASSETS.windrushBanner})`, backgroundPosition: "50% 35%" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/75 via-black/85 to-black" />
        <Container className="relative z-10">
          <FadeIn>
            <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
              The Project Ecosystem
            </p>
            <h1 className="mt-5 font-display font-black uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95] max-w-4xl">
              A decade-long vision <span className="text-amber-400">in motion.</span>
            </h1>
            <p className="mt-7 max-w-2xl text-zinc-300 text-base sm:text-lg leading-relaxed">
              Step inside what's live, what's coming, and what's still being dreamed.
              Funders, venues, festivals and artists — this is where you'll find your way in.
            </p>
            <div className="mt-9 flex flex-wrap gap-3 text-[10px] uppercase tracking-[0.3em] font-bold">
              <a href="#current" className="text-amber-300 hover:text-amber-200 border border-amber-400/40 hover:border-amber-400 px-4 py-2.5">
                Current
              </a>
              <a href="#in-development" className="text-cyan-300 hover:text-cyan-200 border border-cyan-400/40 hover:border-cyan-400 px-4 py-2.5">
                In Development
              </a>
              <a href="#planned" className="text-cyan-300 hover:text-cyan-200 border border-cyan-400/40 hover:border-cyan-400 px-4 py-2.5">
                Planned
              </a>
              <a href="#future-vision" className="text-zinc-300 hover:text-zinc-100 border border-zinc-500/40 border-dashed hover:border-zinc-300 px-4 py-2.5">
                Future Vision
              </a>
            </div>
          </FadeIn>
        </Container>
      </section>

      {/* COLUMNS */}
      {Object.entries(groups).map(([statusKey, group]) => (
        <ProjectColumn key={statusKey} statusKey={statusKey} group={group} />
      ))}

      {/* PARTNER CTA */}
      <SectionDivider label="Build with us" />
      <section className="relative py-20 sm:py-28">
        <Container>
          <FadeIn>
            <div className="border border-amber-400/30 bg-zinc-950 p-10 sm:p-14 text-center">
              <p className="text-[10px] uppercase tracking-[0.3em] text-amber-400 font-bold">
                Funders · venues · festivals · creative partners
              </p>
              <h2 className="mt-4 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
                Want to bring a project to life with us?
              </h2>
              <p className="mt-5 text-zinc-400 text-base max-w-xl mx-auto">
                We co-produce, co-commission and co-tour. Every project on this page is
                actively seeking the right partners.
              </p>
              <Link
                to="/corporate"
                data-testid="projects-partner-cta"
                className="mt-8 inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 text-black px-7 py-4 font-bold uppercase tracking-[0.22em] text-[11px] transition-colors"
              >
                Start a conversation
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </FadeIn>
        </Container>
      </section>

      <StayConnected pageSource="projects" testid="projects-stay" />
    </div>
  );
}

function ProjectColumn({ statusKey, group }) {
  if (!group.items.length) return null;
  const anchor =
    statusKey === "live-now"
      ? "current"
      : statusKey === "in-development"
      ? "in-development"
      : statusKey === "coming-soon"
      ? "planned"
      : "future-vision";
  return (
    <section id={anchor} data-testid={`projects-column-${anchor}`} className="relative py-16 sm:py-20 border-t border-white/5">
      <Container>
        <FadeIn>
          <div className="flex items-baseline justify-between gap-6 mb-10">
            <div>
              <p className="text-[10px] uppercase tracking-[0.4em] text-amber-400 font-bold">
                The Ecosystem
              </p>
              <h2 className="mt-3 font-display font-black uppercase text-3xl sm:text-5xl leading-[0.95]">
                {group.label}
              </h2>
              <p className="mt-3 text-sm text-zinc-400 max-w-xl">{group.description}</p>
            </div>
            <p className="hidden sm:block text-[10px] uppercase tracking-[0.3em] text-zinc-600 shrink-0">
              {group.items.length} {group.items.length === 1 ? "project" : "projects"}
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {group.items.map((p, i) => (
              <FadeIn key={p.id} delay={i * 0.04}>
                <ProjectTile project={p} />
              </FadeIn>
            ))}
          </div>
        </FadeIn>
      </Container>
    </section>
  );
}

function ProjectTile({ project }) {
  const interactive = project.status === "live-now" && !!project.href;
  const isExternal = !!project.external;

  const card = (
    <div className="relative aspect-[4/5] overflow-hidden bg-zinc-900 border border-white/5 group-hover:border-amber-400/40 transition-colors">
      <div
        className="absolute inset-0 bg-cover transition-transform duration-[1500ms] group-hover:scale-110"
        style={{ backgroundImage: `url(${project.image})`, backgroundPosition: project.focal || "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-black/5" />
      <div className="absolute top-4 left-4">
        <StatusBadge status={project.status} />
      </div>
      {isExternal && (
        <div className="absolute top-4 right-4 inline-flex h-7 w-7 items-center justify-center bg-black/60 text-amber-300 border border-amber-400/40">
          <ExternalLink className="h-3.5 w-3.5" />
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-6">
        <h3 className="font-display font-bold uppercase text-xl leading-tight">
          {project.title}
        </h3>
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed line-clamp-3">
          {project.desc}
        </p>
      </div>
    </div>
  );

  if (interactive && isExternal) {
    return (
      <a href={project.href} target="_blank" rel="noopener noreferrer" data-testid={`project-${project.id}`} className="group block">
        {card}
      </a>
    );
  }
  if (interactive) {
    return (
      <Link to={project.href} data-testid={`project-${project.id}`} className="group block">
        {card}
      </Link>
    );
  }
  // For non-live projects we keep the tile non-interactive at the page level —
  // partners contact us via the page-wide CTA at the bottom. (We avoid stacking
  // modals on top of an already dense canvas.)
  return (
    <div data-testid={`project-${project.id}`} className="group block">
      {card}
    </div>
  );
}
